import { call, put, takeLatest, takeEvery, select, debounce, all, delay } from 'redux-saga/effects';

import {
  importSource as importSourceApi,
  deleteSource as deleteSourceApi,
  updateSource as updateSourceApi,
  createSource as createSourceApi,
  getSources as getSourcesApi,
  getSourceTypes as getSourceTypesApi,
  getSourceHealth as getSourceHealthApi,
  getVariables as getVariablesApi,
  getVariable as getVariableApi,
  createVariables as createVariablesApi,
  updateVariable as updateVariableApi,
  deleteVariable as deleteVariableApi,
  uploadAOExcel as uploadAOExcelAPI,
  checkConnectivity as checkConnectivityApi,
  updateTfrClassificationConfiguration as updateTfrClassificationConfigurationApi,
} from '../../services';
import { INTERNAL_DATA_SOURCE } from '../../constants';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import { getSite } from '../sites';
import { REFRESH_VALUES, setPollingActive, setPollingActiveDone } from '../application';
import {
  REQUEST_SOURCES,
  DELETE_SOURCE,
  UPDATE_SOURCE,
  CREATE_SOURCE,
  IMPORT_SOURCE,
  REQUEST_CONNECTIVITY,
  UPDATE_SOURCE_VARIABLE,
  DELETE_SOURCE_VARIABLE,
  REQUEST_SITE_VARIABLES,
  CREATE_SOURCE_VARIABLES,
  UPLOAD_AO_EXCEL,
  UPLOAD_TFR_CLASSIFICIATION_CONFIG,
  receiveSources,
  receieveConnectivity,
  receiveSyncStatus,
  receiveVariable,
  receiveSiteVariables,
  removeVariable,
  importSource,
} from './actions';
import { getSources, getSyncedSources } from './selectors';

export function* doRequestSources({ siteId, withVariables }) {
  try {
    const { values: sources } = yield call(getSourcesApi, siteId);
    const { values: sourceTypes } = yield call(getSourceTypesApi);
    if (withVariables) {
      const variablesArray = yield all(
        sources.map((source) => call(getVariablesApi, source.id, { extended: true }))
      );
      const siteVariables = variablesArray.map((v) => v.values).flat(1);
      const variableIndex = {};
      siteVariables.forEach((v) => {
        variableIndex[v.id] = v;
      });
      yield put(receiveSources(sources, sourceTypes, variableIndex, siteId));
    } else {
      yield put(receiveSources(sources, sourceTypes, null, siteId));
    }
  } catch (e) {
    console.error('Unable to fetch sources: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getSources', 'error')()));
    yield put(receiveSources([], [], {}, siteId));
  }
}

export function* doDeleteSource(action) {
  const { sourceId, siteId } = action;

  try {
    yield deleteSourceApi(sourceId);
    yield doRequestSources({ siteId });
    yield put(displayNotification(getNotification('deleteSource', 'success')(sourceId)));
  } catch (e) {
    console.error('Unable to delete source: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteSource', 'error')(sourceId)));
  }
}

export function* doCreateSource(action) {
  const { data: source } = action;
  const { options: { import: importVars = {} } = {} } = source;
  let created;
  try {
    created = yield createSourceApi(source);
    yield put(displayNotification(getNotification('createSource', 'success')()));
  } catch (e) {
    console.error('Unable to create source: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createSource', 'error')(e.message)));
  }
  if (created && ((importVars.endpoint && importVars.enabled) || created.type === 'dec')) {
    yield put(importSource(source.site, created.id, () => null));
  } else if (created) {
    yield doRequestSources({ siteId: source.site });
  }
}

export function* doUpdateSource(action) {
  const { siteId, sourceId, data, successMessage, errorMessage } = action;

  try {
    yield updateSourceApi(sourceId, data);
    yield doRequestSources({ siteId });
    yield put(
      displayNotification(getNotification('updateSource', 'success')(sourceId, successMessage))
    );
  } catch (e) {
    console.error('Unable to update source: ', e);
    yield call(checkOnline);
    yield put(
      displayNotification(getNotification('updateSource', 'error')(sourceId, errorMessage))
    );
  }
}

export function* doGetSourceSyncHealth(action) {
  yield put(setPollingActive('sources'));
  try {
    const site = yield select((state) => getSite(state, action.siteId));
    const sources = yield select(getSources);
    const syncedSources = yield select(getSyncedSources);

    if (site && sources.some((s) => syncedSources.includes(s.type))) {
      const syncStatus = yield call(getSourceHealthApi, site.id, site.org);
      if (syncStatus) {
        yield put(receiveSyncStatus(action.siteId, syncStatus));
      }
    }
  } catch (e) {
    console.error('Unable to fetch source health: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getSourceSyncHealth', 'error')()));
  }
  yield put(setPollingActiveDone('sources'));
}

export function* doRequestVariable(action) {
  const { sourceId, variableId } = action;
  try {
    const variable = yield call(getVariableApi, sourceId, variableId);
    yield put(receiveVariable(variable));
  } catch (e) {
    console.error('Unable to fetch signal: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getVariables', 'error')()));
  }
}

export function* doRequestVariables() {
  try {
    const sources = yield select(getSources);
    const variablesArray = yield all(
      sources.map((source) => call(getVariablesApi, source.id, { extended: true }))
    );
    const variables = variablesArray.map((v) => v.values).flat(1);
    const variableIndex = {};
    variables.forEach((v) => {
      variableIndex[v.id] = v;
    });
    yield put(receiveSiteVariables(variableIndex));
  } catch (e) {
    console.error('Unable to fetch signals: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getVariables', 'error')()));
  }
}

export function* doImportSourceVariables(action) {
  const {
    siteId,
    sourceId,
    onComplete = () => {
      // do something on onComplete
    },
  } = action;

  try {
    const { added, updated } = yield importSourceApi(sourceId);
    yield call(doRequestVariables);
    const message = `Imported source signals: added (${added}), updated (${updated})`;
    yield put(displayNotification(getNotification('importSourceVars', 'success')(message)));
  } catch (e) {
    console.error('Unable to import source variables: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('importSourceVars', 'error')(e.message)));
  }
  yield doRequestSources({ siteId });
  yield call(onComplete);
}

export function* doCreateSourceVariables(action) {
  const { sourceId, data, successCallback, failureCallback, doNotify } = action;
  const coercedData = Array.isArray(data) ? data : [data];
  const isAttributeType = !!coercedData[0].asset_id;
  const title = isAttributeType ? 'Attribute' : 'Signal';
  const singleCreate = coercedData.length === 1;

  try {
    const { values, errors } = yield createVariablesApi(sourceId, coercedData);

    if (isAttributeType && errors.length) {
      throw new Error(JSON.stringify(errors));
    }
    if (!isAttributeType && singleCreate) {
      yield put(receiveVariable(values[0]));
    } else if (!isAttributeType) {
      yield call(doRequestVariables);
    }

    if (doNotify) {
      yield put(
        displayNotification(
          getNotification('createVariable', 'success')(
            singleCreate ? coercedData[0].name : '',
            title
          )
        )
      );
    }
    if (successCallback) {
      successCallback();
    }
  } catch (e) {
    console.error('Unable to create signal: ', e);
    const handleUniqueNameError = e?.message?.includes('already exists') && failureCallback;
    if (handleUniqueNameError) {
      failureCallback(['Attribute name must be unique to component.']);
    }
    if (doNotify && !handleUniqueNameError) {
      yield call(checkOnline);
      yield put(
        displayNotification(
          getNotification('createVariable', 'error')(singleCreate ? coercedData[0].name : '', title)
        )
      );
    }
  }
}

export function* doUpdateSourceVariable(action) {
  const { sourceId, variableId, data, successCallback, failureCallback, doNotify } = action;
  const title = data.asset_id ? 'Attribute' : 'Signal';
  try {
    const updatedVariable = yield updateVariableApi(sourceId, variableId, data);
    yield put(receiveVariable(updatedVariable));
    yield call(doRequestVariable, { sourceId, variableId });
    if (doNotify) {
      yield put(
        displayNotification(
          getNotification('updateVariable', 'success')(data.name, variableId, title)
        )
      );
    }
    if (successCallback) {
      successCallback();
    }
  } catch (e) {
    console.error('Unable to update variable: ', e);
    const handleUniqueNameError =
      e?.response?.data?.message?.includes('already exists') && failureCallback;
    if (handleUniqueNameError) {
      failureCallback(['Attribute name must be unique to component.']);
    }
    if (doNotify && !handleUniqueNameError) {
      yield call(checkOnline);
      yield put(
        displayNotification(
          getNotification('updateVariable', 'error')(data.name, variableId, title)
        )
      );
    }
  }
}

export function* doDeleteSourceVariable(action) {
  const { sourceId, variableId, callback, doNotify, isAttribute } = action;
  const title = isAttribute ? 'Attribute' : 'Signal';

  try {
    yield deleteVariableApi(sourceId, variableId);
    yield put(removeVariable(variableId));
    if (doNotify) {
      yield put(
        displayNotification(getNotification('deleteVariable', 'success')(variableId, title))
      );
    }
    if (callback) {
      callback();
    }
  } catch (e) {
    console.error('Unable to delete signal: ', e);
    if (doNotify) {
      yield call(checkOnline);
      yield put(displayNotification(getNotification('deleteVariable', 'error')(variableId, title)));
    }
  }
}

export function* doUploadAOExcel(action) {
  const { sourceId, siteId, file, org, onComplete } = action;
  try {
    const fd = new FormData();
    fd.append('site', siteId);
    fd.append('file', file);
    fd.append('org', org);
    const response = yield call(uploadAOExcelAPI, sourceId, fd);
    if (response.status === 1)
      yield put(displayNotification(getNotification('uploadAOExcel', 'success')()));
    else yield put(displayNotification(getNotification('uploadAOExcel', 'warning')()));
  } catch (e) {
    console.error('Error in importing the file.', e);
    yield put(displayNotification(getNotification('uploadAOExcel', 'error')()));
  }
  yield onComplete();
}

function* doUploadTFRClassificationConfiguration(action) {
  const { siteId, sourceId, configuration, fileName } = action;
  try {
    yield call(updateTfrClassificationConfigurationApi, sourceId, configuration, fileName);
    yield doRequestSources({ siteId });
    yield put(displayNotification(getNotification('uploadTFRClassificationConfig', 'success')()));
  } catch (e) {
    console.error('Error in uploading the file.', e);
    yield put(displayNotification(getNotification('uploadTFRClassificationConfig', 'error')()));
  }
}

function* doRequestConnectivity() {
  try {
    const sources = yield select(getSources);
    const filteredSources = sources.filter((source) => source.type !== INTERNAL_DATA_SOURCE); // Ignore internal source
    const connectivity = yield all(filteredSources.map((s) => checkConnectivityApi(s.id)));
    const sourceConnectivityTree = connectivity.filter(
      (sourceConnectivity) => Object.values(sourceConnectivity).length
    );

    const getConnectivityStatus = (sourceTree) => {
      let connected = sourceTree.status;
      if (connected === 0) {
        return connected;
      }

      if (sourceTree.dependencies.length) {
        sourceTree.dependencies.forEach((dep) => {
          if (connected !== 0) {
            connected = getConnectivityStatus(dep);
          }
        });
      }
      return connected;
    };

    const aggregatedConnectivity = sourceConnectivityTree.reduce(
      (acc, source) => ({ ...acc, [source.name]: getConnectivityStatus(source) }),
      {}
    );

    yield put(receieveConnectivity(sourceConnectivityTree, aggregatedConnectivity));
  } catch (e) {
    console.error('Unable to get connectivity status: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('connectivity', 'error')()));
  }
}

export function* doRefreshSources(action) {
  //yield call(doGetSourceSyncHealth, action);
  yield call(doRequestConnectivity);
}

export function* pollForInternalSource(siteId) {
  while (true) {
    const { values: sources } = yield call(getSourcesApi, siteId);
    if (sources.some((source) => source.type === INTERNAL_DATA_SOURCE)) {
      break;
    }
    yield delay(500);
  }
}

export const sagas = [
  takeLatest(REQUEST_SOURCES, doRequestSources),
  takeEvery(DELETE_SOURCE, doDeleteSource),
  takeLatest(UPDATE_SOURCE, doUpdateSource),
  takeLatest(CREATE_SOURCE, doCreateSource),
  takeLatest(IMPORT_SOURCE, doImportSourceVariables),
  takeEvery(REQUEST_SITE_VARIABLES, doRequestVariables),
  takeLatest(CREATE_SOURCE_VARIABLES, doCreateSourceVariables),
  takeLatest(UPDATE_SOURCE_VARIABLE, doUpdateSourceVariable),
  takeLatest(DELETE_SOURCE_VARIABLE, doDeleteSourceVariable),
  takeLatest(UPLOAD_AO_EXCEL, doUploadAOExcel),
  takeLatest(UPLOAD_TFR_CLASSIFICIATION_CONFIG, doUploadTFRClassificationConfiguration),
  takeLatest(REQUEST_CONNECTIVITY, doRequestConnectivity),
  debounce(500, REFRESH_VALUES, doRefreshSources),
];
